import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Dialog, SelectChangeEvent } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSendTripInvitation } from '../../../hooks/useSendTripInvitation';
import { useAuth0 } from '../../../auth/AuthProvider';
import useGetUser from '../../../hooks/useGetUser';
import useGetEmergencyContacts from '../../../hooks/useGetEmergencyContact';
import useGetTravelDocument from '../../../hooks/useTravelDocuments';
import useGetDiveCertificates from '../../../hooks/useGetDiveCertificates';
import useGetDiveEquipment from '../../../hooks/useGetDiveEquipment';
import { format } from 'date-fns';
import useUpsertGuestDetails, {
  GuestDetailsInput,
} from '../../../hooks/useUpsertGuestDetails';
import GuestInvitationSection from './GuestInvitationSection';
import GuestManualEntrySection from './GuestManualEntrySection';
import {
  getFirstInvalidGuestStage,
  validateGuestForm,
  type GuestFormValues,
  type GuestFormErrors,
} from './GuestValidation';
import {
  normalizeCertificateLevel,
  normalizeCertifyingAgency,
} from '../../../constants/diveCertification';
import { normalizeGender } from '../../../constants/gender';
import { RENTAL_GEAR_PRICES } from '../../../constants/rentalGear';
import { useToast } from '../../../common/toast/toastContext';
import { markMagicLinkDeactivated } from '../../../utils/magicLink';
import { getErrorMessage } from '../../../utils/errorUtils';

interface GuestModalProps {
  guestNumber: number;
  guestEmail: string;
  inviteId: number | undefined;
  magicLink?: string;
  bookingId: number;
  tripId: number;
  open: boolean;
  onClose: () => void;
}

const GuestModal: React.FC<GuestModalProps> = ({
  guestNumber,
  guestEmail,
  open,
  bookingId,
  inviteId,
  magicLink,
  tripId,
  onClose,
}) => {
  const [isManual, setIsManual] = useState(guestNumber === 1);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [inviteActive, setInviteActive] = useState(Boolean(inviteId));
  const { user } = useAuth0();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { mutate: upsertGuestDetails, isPending: isSubmitting } =
    useUpsertGuestDetails();

  const [stage, setStage] = useState(1);
  const [needsDiveGear, setNeedsDiveGear] = useState(true);
  const [selectedSizes, setSelectedSizes] = useState<{
    bcd: string;
    wetsuit: string;
    fins: string;
  }>({
    bcd: '',
    wetsuit: '',
    fins: '',
  });

  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({
    mask: false,
    bcd: false,
    wetsuit: false,
    fins: false,
    regulator: false,
    diveComputer: false,
    torch: false,
  });

  const [email, setEmail] = useState(guestEmail);
  const { mutate: sendTripInvitation, isPending: isSending } =
    useSendTripInvitation();
  const handleOpenDeactivateConfirm = () => setIsConfirmDeactivateOpen(true);
  const handleCloseDeactivateConfirm = () => setIsConfirmDeactivateOpen(false);
  const [totalAmount, setTotalAmount] = useState(0);

  const [formValues, setFormValues] = useState<GuestFormValues>({
    fullName: '',
    gender: '',
    email: guestEmail || '',
    phone: '',
    countryOfIssuance: '',
    passportNumber: '',
    expiryDate: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    certificateLevel: '',
    certifyingAgency: '',
    certificateNumber: '',
    airportTransfer: true,
    arrivalFlight: '',
    departureFlight: '',
    arrivalDate: '',
    departureDate: '',
    selfTransferUponArrival: false,
    selfTransferUponDeparture: false,
  });
  const [errors, setErrors] = useState<GuestFormErrors>({});

  useEffect(() => {
    setInviteActive(Boolean(inviteId));
  }, [inviteId, open]);

  const handleInputChange =
    (field: string) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | { value: unknown }>
        | SelectChangeEvent<string>,
    ) => {
      setFormValues(prev => ({
        ...prev,
        [field]: event.target.value as string | boolean,
      }));
    };

  const handlePhoneChange = (field: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (field: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirmDeactivate = () => {
    if (magicLink) {
      markMagicLinkDeactivated(magicLink);
    }
    setInviteActive(false);
    setIsConfirmDeactivateOpen(false);
    showToast({
      message:
        'Invitation link deactivated for claim page. Send a new invite when ready.',
      severity: 'success',
    });
    void queryClient.invalidateQueries({ queryKey: ['tripInvites'] });
  };

  const handleSubmit = () => {
    const validationErrors = validateGuestForm(5, formValues);
    setErrors(validationErrors);

    const firstInvalidStage = getFirstInvalidGuestStage(formValues);
    if (firstInvalidStage) {
      setStage(firstInvalidStage);
      showToast({
        message:
          'Please complete the highlighted guest details before finishing.',
        severity: 'warning',
      });
      return;
    }

    if (Object.keys(validationErrors).length === 0) {
      const rentalGear = needsDiveGear
        ? Object.entries(checkedItems)
            .filter(([, isChecked]) => isChecked)
            .map(([key]) => ({
              key,
              value: {
                size: selectedSizes[key as keyof typeof selectedSizes] || '',
                price: gearPrices[key as keyof typeof gearPrices],
                quantity: 1,
              },
            }))
        : [];

      const guestDetailsInput: GuestDetailsInput = {
        bookingId: bookingId,
        guestNumber: guestNumber,
        fullName: formValues.fullName,
        gender: formValues.gender,
        email: formValues.email,
        phone: formValues.phone,
        certificateLevel: formValues.certificateLevel,
        certificateNumber: formValues.certificateNumber,
        certifyingAgency: formValues.certifyingAgency,
        countryOfIssuance: formValues.countryOfIssuance,
        passportNumber: formValues.passportNumber,
        expiryDate: formValues.expiryDate,
        emergencyContactName: formValues.emergencyContactName,
        emergencyContactPhone: formValues.emergencyContactPhone,
        airportTransferNeeded: formValues.airportTransfer,
        arrivalFlightNumber: formValues.arrivalFlight,
        arrivalDate: formValues.arrivalDate,
        selfTransferUponArrival: formValues.selfTransferUponArrival,
        departureFlightNumber: formValues.departureFlight,
        departureDate: formValues.departureDate,
        selfTransferUponDeparture: formValues.selfTransferUponDeparture,
        rentalGear: rentalGear,
      };

      upsertGuestDetails(guestDetailsInput, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['tripInvites'] });
          showToast({
            message: `Guest ${guestNumber} details saved.`,
            severity: 'success',
          });
          onClose();
        },
        onError: error => {
          console.error('Error submitting guest details:', error);
          showToast({
            message:
              getErrorMessage(error) ||
              'Could not save guest details. Please try again.',
            severity: 'error',
          });
        },
      });
    }
  };

  const handleSendInvitation = () => {
    sendTripInvitation(
      {
        bookingId,
        userId: user?.sub ?? '',
        guestNumber: guestNumber - 1,
        guestEmail: email,
        tripId,
      },
      {
        onSuccess: () => {
          setInviteActive(true);
          showToast({
            message: `Invitation sent to ${email}.`,
            severity: 'success',
          });
        },
        onError: error => {
          showToast({
            message:
              getErrorMessage(error) ||
              'Failed to send invitation. Please try again.',
            severity: 'error',
          });
        },
      },
    );
  };

  const { user: userData, isLoading: userDataLoading } = useGetUser(
    user?.sub ?? '',
    true,
  );

  const {
    travelDocument: travelDocumentData,
    isLoading: travelDocumentLoading,
  } = useGetTravelDocument(user?.sub ?? '', true);

  const {
    emergencyContacts: emergencyContactsData,
    isLoading: emergencyContactsLoading,
  } = useGetEmergencyContacts(user?.sub ?? '', true);

  const { diveCertificates, isLoading: diveCertificatesLoading } =
    useGetDiveCertificates(user?.sub ?? '', true);

  const { diveEquipment, isLoading: diveEquipmentLoading } =
    useGetDiveEquipment(user?.sub ?? '', true);

  const gearPrices = useMemo(() => RENTAL_GEAR_PRICES, []);

  useEffect(() => {
    if (guestNumber === 1) {
      if (userData && !userDataLoading) {
        setFormValues(prev => ({
          ...prev,
          fullName: userData.fullName || '',
          gender: normalizeGender(userData.gender),
          email: userData.email || '',
          phone: userData.phone || '',
        }));
      }

      if (travelDocumentData && !travelDocumentLoading) {
        setFormValues(prev => ({
          ...prev,
          countryOfIssuance: travelDocumentData.countryOfIssuance || '',
          passportNumber: travelDocumentData.passportNumber || '',
          expiryDate: travelDocumentData.expiryDate
            ? format(new Date(travelDocumentData.expiryDate), 'yyyy-MM-dd')
            : '',
        }));
      }

      if (emergencyContactsData && !emergencyContactsLoading) {
        const primaryContact = emergencyContactsData;
        setFormValues(prev => ({
          ...prev,
          emergencyContactName: primaryContact.fullName || '',
          emergencyContactPhone: primaryContact.phone || '',
        }));
      }
    }

    if (guestNumber === 1 && diveCertificates && !diveCertificatesLoading) {
      const primaryCertificate = diveCertificates;
      setFormValues(prev => ({
        ...prev,
        certificateLevel: normalizeCertificateLevel(
          primaryCertificate.certificateLevel,
        ),
        certifyingAgency: normalizeCertifyingAgency(
          primaryCertificate.certifyingAgency,
        ),
        certificateNumber: primaryCertificate.certificateNumber || '',
      }));
    }

    if (guestNumber === 1 && diveEquipment && !diveEquipmentLoading) {
      setCheckedItems({
        bcd: !!diveEquipment.bcd,
        wetsuit: !!diveEquipment.wetsuit,
        fins: !!diveEquipment.fins,
      });
      setSelectedSizes({
        bcd: diveEquipment.bcd || '',
        wetsuit: diveEquipment.wetsuit || '',
        fins: diveEquipment.fins || '',
      });
    }
  }, [
    guestNumber,
    userData,
    travelDocumentData,
    emergencyContactsData,
    userDataLoading,
    travelDocumentLoading,
    emergencyContactsLoading,
    diveCertificates,
    diveCertificatesLoading,
    diveEquipment,
    diveEquipmentLoading,
  ]);

  useEffect(() => {
    const total = Object.entries(checkedItems).reduce(
      (sum, [key, isChecked]) =>
        isChecked
          ? sum + (gearPrices[key as keyof typeof gearPrices] || 0)
          : sum,
      0,
    );
    setTotalAmount(total);
  }, [checkedItems, gearPrices]);

  const handleBack = () => {
    if (stage === 1) {
      setIsManual(false);
    } else {
      setStage(stage - 1);
    }
  };

  const handleNext = () => {
    const validationErrors = validateGuestForm(stage, formValues);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setErrors({});
      setStage(stage + 1);
    }
  };

  const handleGearSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const needsGear = event.target.value === '1';
    setNeedsDiveGear(needsGear);
    if (!needsGear) {
      setCheckedItems({
        mask: false,
        bcd: false,
        wetsuit: false,
        fins: false,
        regulator: false,
        diveComputer: false,
        torch: false,
      });
      setSelectedSizes({ bcd: '', wetsuit: '', fins: '' });
      setTotalAmount(0);
    }
  };

  const handleCheckboxChange =
    (item: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedItems(prev => ({ ...prev, [item]: event.target.checked }));
    };

  const handleChipClick = (item: string, size: string) => () => {
    setSelectedSizes(prev => ({ ...prev, [item]: size }));
  };

  const renderSizeChips = (
    item: keyof typeof selectedSizes,
    sizes: string[],
  ) => (
    <div className="mt-2 flex gap-2">
      {sizes.map(size => (
        <Chip
          key={size}
          label={size}
          onClick={handleChipClick(item, size)}
          color={selectedSizes[item] === size ? 'primary' : 'default'}
        />
      ))}
    </div>
  );

  const handleSwitchToManual = () => {
    setIsManual(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="guest-modal-title"
      aria-describedby="guest-modal-description"
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: theme => ({
            [theme.breakpoints.only('xs')]: {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              height: '100%',
              borderRadius: '24px 24px 0 0',
              alignSelf: 'flex-end',
            },
            [theme.breakpoints.up('md')]: {
              width: '800px',
              borderRadius: '24px',
              maxHeight: '90vh',
            },
          }),
        },
      }}
    >
      <div className="overflow-y-auto flex-grow">
        <div className="sticky top-0 z-20 flex items-center justify-between bg-white p-4 sm:justify-end">
          <span className="text-sm font-semibold text-neutral-800 sm:hidden">
            Guest {guestNumber}
          </span>
          <button
            type="button"
            className="rounded-full p-1 text-neutral-800 transition-colors hover:bg-neutral-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-santtai-primary"
            onClick={onClose}
            aria-label="Close guest details"
          >
            <IconX size={24} />
          </button>
        </div>

        {isManual && (
          <GuestManualEntrySection
            stage={stage}
            formValues={formValues}
            errors={errors}
            needsDiveGear={needsDiveGear}
            checkedItems={checkedItems}
            totalAmount={totalAmount}
            gearPrices={gearPrices}
            guestNumber={guestNumber}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onPhoneChange={handlePhoneChange}
            onDateChange={handleDateChange}
            onCheckboxChange={handleCheckboxChange}
            onGearSelection={handleGearSelection}
            renderSizeChips={renderSizeChips}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={handleSubmit}
          />
        )}

        {!isManual && (
          <GuestInvitationSection
            guestNumber={guestNumber}
            email={email}
            inviteId={inviteId}
            magicLink={magicLink}
            isSending={isSending}
            isConfirmDeactivateOpen={isConfirmDeactivateOpen}
            inviteActive={inviteActive}
            onEmailChange={setEmail}
            onSendInvitation={handleSendInvitation}
            onSwitchToManual={handleSwitchToManual}
            onClose={onClose}
            onOpenDeactivateConfirm={handleOpenDeactivateConfirm}
            onCloseDeactivateConfirm={handleCloseDeactivateConfirm}
            onConfirmDeactivate={handleConfirmDeactivate}
          />
        )}
      </div>
    </Dialog>
  );
};

export default GuestModal;
